import { db } from '../config/firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  getDoc,
  limit,
} from 'firebase/firestore';
import { User, Role } from '@easydone/shared';

/**
 * Get all users for an organization
 */
export async function getOrganizationUsers(orgId: string): Promise<User[]> {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('orgId', '==', orgId), limit(1000));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as User));
}

/**
 * Get a single user by ID
 */
export async function getUser(userId: string): Promise<User | null> {
  const userRef = doc(db, 'users', userId);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as User;
}

/**
 * Update user role
 */
export async function updateUserRole(userId: string, role: Role): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    role,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update user sites assignment
 */
export async function updateUserSites(userId: string, siteIds: string[]): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    assignedSiteIds: siteIds,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Deactivate a user
 */
export async function deactivateUser(userId: string): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    status: 'inactive',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Reactivate a user
 */
export async function reactivateUser(userId: string): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    status: 'active',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a user
 */
export async function deleteUser(userId: string): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await deleteDoc(userRef);
}

/**
 * Invitation data
 */
export interface InvitationData {
  email: string;
  role: Role;
  orgId: string;
  invitedBy: string;
  assignedSiteIds?: string[];
}

/**
 * Create a user invitation
 */
export async function createInvitation(data: InvitationData): Promise<string> {
  const invitationsRef = collection(db, 'invitations');

  const invitation = {
    ...data,
    status: 'pending',
    createdAt: serverTimestamp(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
  };

  const docRef = await addDoc(invitationsRef, invitation);
  return docRef.id;
}

/**
 * Get pending invitations for an organization
 */
export async function getOrganizationInvitations(orgId: string) {
  const invitationsRef = collection(db, 'invitations');
  const q = query(
    invitationsRef,
    where('orgId', '==', orgId),
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Cancel an invitation
 */
export async function cancelInvitation(invitationId: string): Promise<void> {
  const invitationRef = doc(db, 'invitations', invitationId);
  await updateDoc(invitationRef, {
    status: 'cancelled',
    updatedAt: serverTimestamp(),
  });
}
